/**
 * brand_logo.ts — 브랜드 로고 4칸(심볼·전체형 × 라이트·다크) 순수 로직. Firebase 의존 없음.
 *
 *   저장: system_settings/company_info.logos = { full_light, full_dark, symbol_light, symbol_dark } (각 data URL 또는 "").
 *   옛 단일 칸 `logo` 는 full_light 와 같은 값으로 계속 써서(saveBrand 가 둘 다 기록) 옛 번들·스크립트 호환을 지킨다.
 *
 *   그리기는 components/ui/BrandLogo — SVG 는 인라인이라 글자 currentColor 가 자리의 글자색을 따른다. 그래서 보통 라이트 한 장이면
 *   다크 자리도 해결되고, 다크 칸은 색 자동 대응이 안 되는 PNG/JPG 나 어두운 배경 전용 디자인을 올릴 때만 쓴다.
 *
 *   고르기(pickLogo): 요청한 칸이 비어 있으면 같은 모양의 다른 테마 → 전체형 같은 테마 → 전체형 다른 테마 순으로 대체.
 *   네 장 다 올리지 않아도 한 장만 있으면 모든 자리에 보인다.
 */
export const LOGO_SLOT_KEYS = ["full_light", "full_dark", "symbol_light", "symbol_dark"] as const;
export type LogoSlotKey = (typeof LOGO_SLOT_KEYS)[number];
export type LogoSlots = Record<LogoSlotKey, string>;

export type LogoVariant = "full" | "symbol";
export type LogoTheme = "light" | "dark";

export const EMPTY_LOGO_SLOTS: LogoSlots = { full_light: "", full_dark: "", symbol_light: "", symbol_dark: "" };

/** 칸 설명 — 브랜드 화면 그리드 라벨·안내 */
export const LOGO_SLOT_META: Record<LogoSlotKey, { label: string; hint: string; variant: LogoVariant; theme: LogoTheme }> = {
  full_light: { label: "로고+텍스트 · 라이트", hint: "밝은 배경 — 로그인 화면", variant: "full", theme: "light" },
  full_dark: { label: "로고+텍스트 · 다크", hint: "어두운 배경 — 좌측바", variant: "full", theme: "dark" },
  symbol_light: { label: "심볼 · 라이트", hint: "밝은 배경 — 좁은 자리", variant: "symbol", theme: "light" },
  symbol_dark: { label: "심볼 · 다크", hint: "어두운 배경 — 좁은 자리", variant: "symbol", theme: "dark" },
};

/** web/public 에 들어 있는 기본 로고 파일 — "기본 로고 채우기" 가 가져와 data URL 로 저장한다 */
export const DEFAULT_LOGO_FILES: Record<LogoSlotKey, string> = {
  full_light: "/logo-full.svg", // 글자 currentColor — 인라인(BrandLogo)으로 그리면 어두운 배경에서도 흰 글자
  full_dark: "/logo-full.svg",
  symbol_light: "/favicon.svg", // 파비콘과 같은 글래스 심볼(반투명 파스텔) — 밝은·어두운 배경 공용
  symbol_dark: "/favicon.svg",
};

export function slotKey(variant: LogoVariant, theme: LogoTheme): LogoSlotKey {
  return `${variant}_${theme}` as LogoSlotKey;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** Firestore 값 → LogoSlots. `logos` 가 없으면 옛 `logo` 를 full_light 로 읽는다 */
export function parseLogoSlots(logos: unknown, legacyLogo: unknown): LogoSlots {
  const o = (logos && typeof logos === "object" ? logos : {}) as Record<string, unknown>;
  const out: LogoSlots = { ...EMPTY_LOGO_SLOTS };
  for (const k of LOGO_SLOT_KEYS) out[k] = str(o[k]);
  if (!out.full_light) out.full_light = str(legacyLogo);
  return out;
}

/** 자리에 맞는 로고 하나 고르기(대체 순서는 머리말). 아무것도 없으면 "" */
export function pickLogo(slots: LogoSlots, variant: LogoVariant, theme: LogoTheme): string {
  const other: LogoTheme = theme === "light" ? "dark" : "light";
  const order: LogoSlotKey[] =
    variant === "symbol"
      ? [slotKey("symbol", theme), slotKey("symbol", other), slotKey("full", theme), slotKey("full", other)]
      : [slotKey("full", theme), slotKey("full", other), slotKey("symbol", theme), slotKey("symbol", other)];
  for (const k of order) if (slots[k]) return slots[k];
  return "";
}

export function hasAnyLogo(slots: LogoSlots): boolean {
  return LOGO_SLOT_KEYS.some((k) => !!slots[k]);
}

export function sameLogoSlots(a: LogoSlots, b: LogoSlots): boolean {
  return LOGO_SLOT_KEYS.every((k) => a[k] === b[k]);
}
